import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { ArrowRight, Target, Zap } from "lucide-react";

const eur = (v) => `${Math.round(Number(v) || 0).toLocaleString("pt-PT")} €`;

function Delta({ actual, target }) {
  const t = Number(target) || 0;
  const d = (Number(actual) || 0) - t;
  if (t <= 0) return <span className="text-[#555]">—</span>;
  const ok = d >= 0;
  return (
    <span className={`font-display font-black ${ok ? "text-[#22C55E]" : "text-[#E50000]"}`}>
      {ok ? "+" : ""}{Math.round(d * 10) / 10}
    </span>
  );
}

function Bar({ actual, target }) {
  const t = Number(target) || 0;
  const pct = t > 0 ? Math.min(100, Math.round(((Number(actual) || 0) / t) * 100)) : 0;
  return (
    <div className="h-1.5 bg-[#1A1A1A] w-full overflow-hidden">
      <div className={`h-full transition-all duration-700 ${pct >= 100 ? "bg-[#22C55E]" : pct >= 70 ? "bg-[#E5A000]" : "bg-[#E50000]"}`}
        style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function PerformanceGaps({ playerId, year, compact = false }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    const q = year ? `?year=${year}` : "";
    api.get(playerId ? `/gps/performance/${playerId}${q}` : `/gps/performance${q}`)
      .then((r) => setD(r.data)).catch(() => setD({ hidden: true }));
  }, [playerId, year]);

  if (!d) return null;
  if (d.hidden) return null;

  if (!d.configured) {
    return (
      <div className="kw-card p-5 flex items-center gap-4" data-testid="performance-gaps-empty">
        <Target className="w-6 h-6 text-[#E50000] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline">Desvios face ao GPS</div>
          <div className="text-sm text-[#A3A3A3] mt-1">
            Preenche no GPS 1-3-5 o objetivo em euros, o ticket médio e as tuas conversões. A partir daí
            comparo a tua atividade com o ritmo necessário e digo-te o que falta para recuperar.
          </div>
        </div>
        <Link to="/my-profile#gps" className="kw-btn-secondary text-xs inline-flex items-center gap-2"
          data-testid="performance-gaps-setup">Configurar GPS <ArrowRight className="w-3 h-3" /></Link>
      </div>
    );
  }

  const behind = d.metrics.filter((m) => m.recovery.remaining > 0);
  const daysLeft = d.metrics[0]?.recovery.working_days_left || 0;
  const daysLabel = daysLeft === 1 ? "1 dia útil" : `${daysLeft} dias úteis`;

  if (compact) {
    return (
      <div className="kw-card p-5 space-y-3" data-testid="performance-gaps-compact">
        <div className="flex items-center justify-between gap-3">
          <div className="kw-overline">Ritmo GPS · semana {d.week_label}</div>
          <Link to="/o-meu-desempenho" className="text-[10px] uppercase tracking-widest text-[#E50000] inline-flex items-center gap-1"
            data-testid="performance-gaps-open">Ver desvios <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {d.metrics.map((m) => (
            <div key={m.key} data-testid={`gap-compact-${m.key}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">{m.label}</div>
              <div className="font-display text-2xl font-black">
                {m.week.actual}<span className="text-sm text-[#555]"> / {m.week.target}</span>
              </div>
              <Bar actual={m.week.actual} target={m.week.target_to_date} />
            </div>
          ))}
        </div>
        {behind.length > 0 && (
          <p className="text-xs text-[#A3A3A3]">
            Para o mês fechar no ritmo, faltam{" "}
            {behind.map((m, i) => (
              <span key={m.key}>
                {i > 0 ? ", " : ""}<span className="text-white">{m.recovery.remaining} {m.label.toLowerCase()}</span>
              </span>
            ))}{" "}
            em {daysLabel}.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="performance-gaps">
      <div className="kw-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#737373] border-b border-[#1F1F1F]">
              <th className="text-left p-3">Atividade</th>
              <th className="text-right p-3">Semana ({d.week_label})</th>
              <th className="text-right p-3">Mês</th>
              <th className="text-right p-3">Ano</th>
              <th className="text-right p-3">Desvio do mês</th>
            </tr>
          </thead>
          <tbody>
            {d.metrics.map((m) => (
              <tr key={m.key} className="border-b border-[#141414] hover:bg-[#0E0E0E]" data-testid={`gap-row-${m.key}`}>
                <td className="p-3 font-display font-black uppercase tracking-tight">{m.label}</td>
                {["week", "month", "year"].map((p) => (
                  <td key={p} className="p-3 text-right">
                    <div><span className="font-display text-lg font-black">{m[p].actual}</span>
                      <span className="text-xs text-[#555]"> / {m[p].target}</span></div>
                    <div className="w-24 ml-auto mt-1"><Bar actual={m[p].actual} target={m[p].target_to_date} /></div>
                    <div className="text-[10px] text-[#737373] mt-0.5">esperado até hoje: {m[p].target_to_date}</div>
                  </td>
                ))}
                <td className="p-3 text-right"><Delta actual={m.month.actual} target={m.month.target_to_date} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {behind.length > 0 && (
        <div className="kw-card p-5 border-l-4 border-[#E50000]" data-testid="performance-recovery">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#E50000]" />
            <div className="kw-overline">Plano de recuperação · falta{daysLeft === 1 ? "" : "m"} {daysLabel} este mês</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {behind.map((m) => (
              <div key={m.key} data-testid={`recovery-${m.key}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#737373]">{m.label}</div>
                <div className="font-display text-3xl font-black">{m.recovery.per_day}<span className="text-sm text-[#555]">/dia</span></div>
                <div className="text-xs text-[#A3A3A3]">faltam {m.recovery.remaining} para fechar o mês no ritmo</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.has_money_goal && (
        <div className="kw-card p-5 space-y-4" data-testid="performance-money">
          <div className="kw-overline">Volume em euros</div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">{d.money.month_label}</div>
              <div className="font-display text-2xl font-black">{eur(d.money.month.actual)}</div>
              <div className="text-xs text-[#555]">objetivo {eur(d.money.month.target)}</div>
              <div className="mt-2"><Bar actual={d.money.month.actual} target={d.money.month.target} /></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">Ano acumulado</div>
              <div className="font-display text-2xl font-black">{eur(d.money.year.actual)}</div>
              <div className="text-xs text-[#555]">objetivo {eur(d.money.year.target)}</div>
              <div className="mt-2"><Bar actual={d.money.year.actual} target={d.money.year.target} /></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">Para chegar ao objetivo</div>
              <div className="font-display text-2xl font-black">{eur(d.money.recovery.per_month)}<span className="text-sm text-[#555]">/mês</span></div>
              <div className="text-xs text-[#A3A3A3]">faltam {eur(d.money.recovery.remaining)} em {d.money.recovery.months_left} meses</div>
            </div>
          </div>
          <p className="text-[10px] text-[#555]">A faturação real vem do que registas no GPS 4-1-1, mês a mês.</p>
        </div>
      )}
    </div>
  );
}
